import React, { useCallback, useMemo, useState } from "react";
import { brand } from "../../../theme/theme";
import PropTypes from "prop-types";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Inbox } from "lucide-react";

import { Avatar } from "../../../components/Avatar";
import { Badge } from "../../../components/Badge";
import { DataTable } from "../../../components/DataTable";
import { ToggleSwitch } from "../../../components/ToggleSwitch";
import { useAuth } from "../../../components/contexts/AuthContext";
import { getData } from "../../../components/hooks/getData";
import { useTheme as useCustomTheme } from "../../../theme/useTheme";
import { getAvatarImageUrl } from "../../../utils/avatarUtils";
import { CLIENTIFY_CONTACTS_ENDPOINT } from "../../../services/clientifyService";

const TABLE_COLUMNS = [
  "Nombre",
  "Fecha de creación",
  "Propietario",
  "Estado",
  "Lead Scoring",
  "Último contacto",
  "Fuente UTM",
  "Teléfono",
  "Correo",
  "Canal online",
  "Origen de contacto",
  "Última vista",
  "Marketing",
];

const STATUS_LABELS = {
  other: "Otro",
  visitor: "Visitante",
  "not-qualified-lead": "Lead no calificado",
  "cold-lead": "Lead frío",
  "warm-lead": "Lead templado",
  "hot-lead": "Lead caliente",
  "in-deal": "En oportunidad",
  "lost-lead": "Lead perdido",
  client: "Cliente",
  "lost-client": "Cliente perdido",
};

const STATUS_COLOR_KEYS = {
  "cold-lead": "coldLead",
  "warm-lead": "warmLead",
  "hot-lead": "hotLead",
  "in-deal": "inDeal",
  "lost-lead": "lostLead",
  client: "client",
  "lost-client": "lostClient",
};

const formatDate = (dateString) => {
  if (!dateString) return "-";

  try {
    const date = new Date(dateString);
    const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${month}. ${day}, ${year} - ${hours}:${minutes}`;
  } catch {
    return "-";
  }
};

const getStatusLabel = (status) => {
  return STATUS_LABELS[status] || status || "-";
};

const getStatusBadgePalette = (status, statusBadges) => {
  const colorKey = STATUS_COLOR_KEYS[status];
  if (!colorKey) return statusBadges?.default || null;
  return statusBadges?.[colorKey] || statusBadges?.default || null;
};

const getChannelLabel = (channel) => {
  const channelMap = {
    "manual": "Manual",
    "webform": "Formulario web",
    "email": "Email",
    "phone": "Teléfono",
    "social": "Redes sociales",
  };
  return channelMap[channel] || channel || "-";
};

export const Vistatabla = ({
  searchTerm,
  ownerId,
  orderBy,
  refreshKey = 0,
  onOpenContact,
  toolbarProps,
}) => {
  const theme = useTheme();
  const { neutrals, colors } = useCustomTheme();
  const { interceptorReady } = useAuth();

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [failedAvatarImages, setFailedAvatarImages] = useState({});

  React.useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, ownerId, orderBy]);

  const endpoint = useMemo(() => {
    const params = new URLSearchParams();

    params.append("page", String(currentPage + 1));
    params.append("page_size", String(rowsPerPage));
    params.append("order_by", orderBy || "-created");

    if (searchTerm && searchTerm.trim()) {
      params.append("search", searchTerm.trim());
    }

    if (ownerId) {
      params.append("owner_id", String(ownerId));
    }

    return `${CLIENTIFY_CONTACTS_ENDPOINT}?${params.toString()}`;
  }, [currentPage, rowsPerPage, searchTerm, ownerId, orderBy]);

  const { data: contactsData, isLoading, error } = getData(
    endpoint,
    interceptorReady,
    [refreshKey]
  );

  const rows = contactsData?.results || [];
  const totalCount = contactsData?.count || 0;
  const statusBadges = colors?.clientifyTable?.statusBadges || {};
  const stickyDividerColor = theme.palette.border?.input || theme.palette.divider;

  const stickyHeaderCellSx = {
    position: "sticky",
    left: 0,
    zIndex: 6,
    backgroundColor: neutrals.gray[50],
    borderRight: `1px solid ${stickyDividerColor}`,
    minWidth: { xs: 220, md: 280 },
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      right: 0,
      width: "1px",
      height: "100%",
      backgroundColor: stickyDividerColor,
      pointerEvents: "none",
      zIndex: 7,
    },
  };

  const stickyBodyCellSx = {
    position: "sticky",
    left: 0,
    zIndex: 3,
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${stickyDividerColor}`,
    minWidth: { xs: 220, md: 280 },
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      right: 0,
      width: "1px",
      height: "100%",
      backgroundColor: stickyDividerColor,
      pointerEvents: "none",
      zIndex: 4,
    },
  };

  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (nextRowsPerPage) => {
    setRowsPerPage(nextRowsPerPage);
    setCurrentPage(0);
  };

  const markAvatarImageAsFailed = useCallback((key) => {
    setFailedAvatarImages((prev) => {
      if (prev[key]) return prev;
      return { ...prev, [key]: true };
    });
  }, []);

  const getContactDisplay = (contact) => {
    const fullName = contact.full_name || `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "-";
    const ownerImage = getAvatarImageUrl(contact.owner_picture);
    const contactImage = getAvatarImageUrl(contact.picture_url);
    const contactImageKey = `contact-${contact.id}`;
    const ownerImageKey = `owner-${contact.id}`;
    return {
      fullName,
      primaryEmail: contact.emails && contact.emails.length > 0 ? contact.emails[0].email : "-",
      primaryPhone: contact.phones && contact.phones.length > 0 ? contact.phones[0].phone : "-",
      ownerImage,
      contactImage,
      isMarketingActive: contact.marketing_status === 2,
      statusPalette: getStatusBadgePalette(contact.status, statusBadges),
      contactImageKey,
      ownerImageKey,
      shouldShowContactImage: Boolean(contactImage && !failedAvatarImages[contactImageKey]),
      shouldShowOwnerImage: Boolean(ownerImage && !failedAvatarImages[ownerImageKey]),
    };
  };

  const columns = [
      {
        key: "name",
        header: TABLE_COLUMNS[0],
        headerSx: stickyHeaderCellSx,
        cellSx: stickyBodyCellSx,
        render: (contact) => {
          const d = getContactDisplay(contact);
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {d.shouldShowContactImage ? (
                <Box
                  component="img"
                  src={d.contactImage}
                  alt={d.fullName}
                  onError={() => markAvatarImageAsFailed(d.contactImageKey)}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    objectFit: "cover",
                    display: "block",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Avatar
                  name={d.fullName}
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: "0.6875rem",
                    backgroundColor: brand.avatar,
                    color: "#fff",
                    border: "none",
                  }}
                />
              )}
              <Box sx={{ maxWidth: { xs: 140, md: 200 } }}>
                <Tooltip
                  title={d.fullName}
                  arrow
                  slotProps={{ tooltip: { sx: { textAlign: "center" } } }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                    }}
                  >
                    {d.fullName}
                  </Typography>
                </Tooltip>
              </Box>
            </Stack>

          );
        },
      },
      {
        key: "created",
        header: TABLE_COLUMNS[1],
        render: (contact) => {
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
              {formatDate(contact.created)}
            </Typography>

          );
        },
      },
      {
        key: "owner",
        header: TABLE_COLUMNS[2],
        render: (contact) => {
          const d = getContactDisplay(contact);
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {d.shouldShowOwnerImage ? (
                <Box
                  component="img"
                  src={d.ownerImage}
                  alt={contact.owner_name || contact.owner}
                  onError={() => markAvatarImageAsFailed(d.ownerImageKey)}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    objectFit: "cover",
                    display: "block",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Avatar
                  name={contact.owner_name || contact.owner}
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: "0.6875rem",
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.contrastText,
                    border: "none",
                  }}
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: { xs: 100, md: 150 },
                }}
              >
                {contact.owner_name || contact.owner || "-"}
              </Typography>
            </Stack>

          );
        },
      },
      {
        key: "status",
        header: TABLE_COLUMNS[3],
        render: (contact) => {
          const d = getContactDisplay(contact);
          return (
            <Badge
              sx={{
                backgroundColor: d.statusPalette?.background || undefined,
                color: d.statusPalette?.text || undefined,
              }}
            >
              {getStatusLabel(contact.status)}
            </Badge>

          );
        },
      },
      {
        key: "lead_scoring",
        header: TABLE_COLUMNS[4],
        render: (contact) => {
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
              {contact.lead_scoring || 0}
            </Typography>

          );
        },
      },
      {
        key: "last_contact",
        header: TABLE_COLUMNS[5],
        render: (contact) => {
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
              {formatDate(contact.last_contact)}
            </Typography>

          );
        },
      },
      {
        key: "utm_source",
        header: TABLE_COLUMNS[6],
        render: (contact) => {
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
              {contact.utm_source || "-"}
            </Typography>

          );
        },
      },
      {
        key: "phone",
        header: TABLE_COLUMNS[7],
        render: (contact) => {
          const d = getContactDisplay(contact);
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
              {d.primaryPhone}
            </Typography>

          );
        },
      },
      {
        key: "email",
        header: TABLE_COLUMNS[8],
        render: (contact) => {
          const d = getContactDisplay(contact);
          return (
            <Box sx={{ maxWidth: { xs: 140, md: 200 } }}>
              <Tooltip
                title={d.primaryEmail}
                arrow
                slotProps={{ tooltip: { sx: { textAlign: "center" } } }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.75rem", md: "0.875rem" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {d.primaryEmail}
                </Typography>
              </Tooltip>
            </Box>

          );
        },
      },
      {
        key: "medium",
        header: TABLE_COLUMNS[9],
        render: (contact) => {
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
              {contact.medium || "-"}
            </Typography>

          );
        },
      },
      {
        key: "channel",
        header: TABLE_COLUMNS[10],
        render: (contact) => {
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
              {getChannelLabel(contact.channel)}
            </Typography>

          );
        },
      },
      {
        key: "last_seen",
        header: TABLE_COLUMNS[11],
        render: (contact) => {
          return (
            <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
              {formatDate(contact.last_seen)}
            </Typography>

          );
        },
      },
      {
        key: "marketing",
        header: TABLE_COLUMNS[12],
        render: (contact) => {
          const d = getContactDisplay(contact);
          return (
            <ToggleSwitch
              checked={d.isMarketingActive}
              disabled={true}
              sx={{ pointerEvents: "none" }}
            />

          );
        },
      },
  ];

  return (
    <DataTable
      {...toolbarProps}
      toolbarStart={
        !isLoading ? (
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              color: theme.palette.text.primary,
              whiteSpace: "nowrap",
            }}
          >
            Mis Contactos: {totalCount}
          </Typography>
        ) : null
      }
      columns={columns}
      rows={rows}
      loading={isLoading}
      emptyIcon={Inbox}
      emptyMessage={
        error
          ? "Error al cargar contactos. Intenta nuevamente."
          : searchTerm
            ? "No se encontraron contactos con los filtros"
            : "No hay contactos disponibles"
      }
      onRowClick={(contact) => onOpenContact(contact.id)}
      getRowSx={() => ({
        "&:hover > td:first-of-type": {
          backgroundColor: theme.palette.action.hover,
        },
      })}
      tableMinWidth={350}
      maxHeight="calc(100vh - 295px)"
      page={currentPage}
      rowsPerPage={rowsPerPage}
      totalCount={totalCount}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
    />
  );
};

Vistatabla.propTypes = {
  searchTerm: PropTypes.string,
  ownerId: PropTypes.number,
  orderBy: PropTypes.string,
  refreshKey: PropTypes.number,
  onOpenContact: PropTypes.func.isRequired,
  toolbarProps: PropTypes.object,
};
